/**
 * Create-time customer-specific delivery days & cutoff times.
 *
 * Reuses the Edit-chat delivery modal's UI (`DeliveryDaySettings`,
 * `CutoffTypeSelect`) and its per-day `Record<Day, …>` data shape, but bound
 * directly to the main create form rather than an isolated modal form — so the
 * create flow can apply the values through the SAME explicit mutation contract
 * (`updateSupplierDeliverySetting`, chat scope) after `chatCreate`.
 *
 * Delivery settings require a distributor: without one the chat has no
 * distributor-scoped delivery setting to write, so the section shows a hint.
 */

import type { CreateChatFormData, CutoffTime, Day } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiClock } from 'react-icons/fi';

import { DeliveryDaySettings } from '../../../../../../components/DeliveryDaySettings';
import { ChocoCheckbox } from '../../../../../../components/FormComponents';
import { DELIVERY_DAYS_ORDER } from '../../../../constants/deliveryDefaults';
import { CutoffTypeSelect } from '../../../EditChat/components/tabs/settings/components/CutoffTypeSelect';

export const CreateDeliverySettingsSection: React.FC = () => {
  const { watch, setValue } = useFormContext<CreateChatFormData>();

  const hasDistributor = Boolean(watch('supplier'));
  const hasCustomSettings = watch('hasCustomChatDeliverySettings');
  const deliveryDays = watch('deliveryDays');
  const cutoffType = watch('cutoffType');
  const cutoffTimes = watch('cutoffTimes');
  const cutoffDays = watch('cutoffDays');

  const handleToggleCustomSettings = useCallback(
    (isSelected: boolean) => {
      setValue('hasCustomChatDeliverySettings', isSelected, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleDayToggle = useCallback(
    (day: Day) => {
      setValue(`deliveryDays.${day}`, !deliveryDays[day]);
    },
    [setValue, deliveryDays]
  );

  const handleCutoffTimeChange = useCallback(
    (day: Day, time: CutoffTime) => {
      setValue(`cutoffTimes.${day}`, time);
    },
    [setValue]
  );

  const handleCutoffDayChange = useCallback(
    (day: Day, cutoffDay: string) => {
      setValue(`cutoffDays.${day}`, cutoffDay);
    },
    [setValue]
  );

  return (
    <Card data-testid="CreateDeliverySettingsSection">
      <CardBody className="space-y-4">
        <div className="flex items-center gap-2">
          <FiClock className="text-primary" />
          <h4 className="font-medium text-foreground">
            Delivery Days & Cutoff Times
          </h4>
        </div>

        {!hasDistributor ? (
          <p className="text-sm text-default-500">
            Select a distributor to set customer-specific delivery days and
            cutoff times for this chat.
          </p>
        ) : (
          <div className="space-y-6">
            <ChocoCheckbox
              isSelected={hasCustomSettings}
              onValueChange={handleToggleCustomSettings}
              size="sm"
              label="Enable customer specific delivery days & cutoff settings"
              description="Set values that differ from the distributor's company settings. Applies to all products in this chat."
            />

            {hasCustomSettings && (
              <div className="space-y-6">
                <CutoffTypeSelect />

                <div className="space-y-4">
                  {DELIVERY_DAYS_ORDER.map((day) => (
                    <DeliveryDaySettings
                      key={day}
                      day={day}
                      isEnabled={deliveryDays[day] || false}
                      cutoffTime={cutoffTimes[day] || '23:59:59'}
                      cutoffDay={cutoffDays[day] || '1'}
                      onToggle={handleDayToggle}
                      onCutoffTimeChange={handleCutoffTimeChange}
                      onCutoffDayChange={handleCutoffDayChange}
                      cutoffType={cutoffType}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
